// src/modules/cad/dxf-style.js

/**
 * ============================================================
 * DXF Style Parser
 * ============================================================
 *
 * Membaca style langsung dari ASCII DXF:
 *
 * Group Code:
 * 5   = Entity Handle
 * 8   = Layer
 * 62  = ACI Color Index
 * 420 = True Color (24-bit RGB)
 *
 * Prioritas warna:
 *
 * 1. True Color 420
 * 2. Entity ACI Color 62
 * 3. Layer ACI Color
 * 4. Default
 *
 * Catatan:
 * - 0   = BYBLOCK
 * - 256 = BYLAYER
 * - 257 = BYOBJECT
 */

const DEFAULT_COLOR = "#222222";

/**
 * ============================================================
 * Public API
 * ============================================================
 */

export async function parseDXFStyles(file) {
    if (!file) {
        throw new Error("[DXF Style] File tidak tersedia.");
    }

    const name = String(file.name || "").toLowerCase();

    if (!name.endsWith(".dxf")) {
        console.warn(
            "[DXF Style] File bukan DXF:",
            file.name
        );
    }

    /**
     * --------------------------------------------------------
     * ASCII DXF
     * --------------------------------------------------------
     */

    const text = await file.text();

    if (!text || !text.trim()) {
        throw new Error(
            "[DXF Style] DXF kosong atau tidak dapat dibaca."
        );
    }

    /**
     * Binary DXF detection
     *
     * Binary DXF biasanya memiliki header:
     * "AutoCAD Binary DXF"
     */

    if (
        text.includes("AutoCAD Binary DXF")
    ) {
        console.warn(
            "[DXF Style] Binary DXF terdeteksi. " +
            "Style parser ASCII tidak digunakan."
        );

        return createEmptyStyleInfo();
    }

    console.log(
        "[DXF Style] Parsing:",
        file.name
    );

    /**
     * Parse group-code pairs.
     */

    const pairs = parsePairs(text);

    console.log(
        "[DXF Style] Group pairs:",
        pairs.length
    );

    /**
     * --------------------------------------------------------
     * Layer colors
     * --------------------------------------------------------
     */

    const layers = parseLayers(pairs);

    console.log(
        "[DXF Style] Layers:",
        layers.size
    );

    /**
     * --------------------------------------------------------
     * Entity colors
     * --------------------------------------------------------
     */

    const entities = parseEntities(
        pairs,
        layers
    );

    console.log(
        "[DXF Style] Styled entities:",
        entities.size
    );

    /**
     * Statistics
     */

    const stats = {
        layers: layers.size,
        entities: entities.size,
        trueColor: 0,
        aciColor: 0,
        byLayer: 0,
        byBlock: 0,
        defaultColor: 0
    };

    for (const style of entities.values()) {
        if (style.trueColor !== null) {
            stats.trueColor++;
        } else if (
            style.aciColor !== null &&
            style.aciColor !== 0 &&
            style.aciColor !== 256
        ) {
            stats.aciColor++;
        } else if (
            style.aciColor === 256
        ) {
            stats.byLayer++;
        } else if (
            style.aciColor === 0
        ) {
            stats.byBlock++;
        } else {
            stats.defaultColor++;
        }
    }

    console.log(
        "[DXF Style] Statistics:"
    );

    console.table(stats);

    return {
        fileName: file.name,
        layers,
        entities,
        stats
    };
}


/**
 * ============================================================
 * Empty style info
 * ============================================================
 */

function createEmptyStyleInfo() {
    return {
        fileName: null,
        layers: new Map(),
        entities: new Map(),
        stats: {
            layers: 0,
            entities: 0,
            trueColor: 0,
            aciColor: 0,
            byLayer: 0,
            byBlock: 0,
            defaultColor: 0
        }
    };
}


/**
 * ============================================================
 * Parse DXF group-code pairs
 * ============================================================
 *
 * DXF ASCII:
 *
 * 0
 * SECTION
 * 2
 * ENTITIES
 *
 * menjadi:
 *
 * {
 *   code: 0,
 *   value: "SECTION"
 * }
 */

function parsePairs(text) {
    const lines =
        text.replace(/\r\n/g, "\n")
            .replace(/\r/g, "\n")
            .split("\n");

    const pairs = [];

    for (
        let i = 0;
        i + 1 < lines.length;
        i += 2
    ) {
        const codeText =
            lines[i].trim();

        const value =
            lines[i + 1].trim();

        const code =
            Number(codeText);

        if (
            !Number.isFinite(code)
        ) {
            continue;
        }

        pairs.push({
            code,
            value
        });
    }

    return pairs;
}


/**
 * ============================================================
 * Parse LAYER table
 * ============================================================
 *
 * Mencari:
 *
 * 0
 * LAYER
 * 2
 * LayerName
 * 62
 * 1
 *
 * hasil:
 *
 * layers.set("LayerName", {
 *     aciColor: 1,
 *     trueColor: null
 * })
 */

function parseLayers(pairs) {
    const layers = new Map();

    let section = null;
    let currentLayer = null;

    for (let i = 0; i < pairs.length; i++) {
        const pair = pairs[i];

        /**
         * ----------------------------------------------------
         * SECTION
         * ----------------------------------------------------
         */

        if (
            pair.code === 0 &&
            pair.value === "SECTION"
        ) {
            const next =
                pairs[i + 1];

            if (
                next &&
                next.code === 2
            ) {
                section =
                    String(next.value)
                        .toUpperCase();
            }

            continue;
        }

        /**
         * ----------------------------------------------------
         * ENDSEC
         * ----------------------------------------------------
         */

        if (
            pair.code === 0 &&
            pair.value === "ENDSEC"
        ) {
            section = null;
            currentLayer = null;
            continue;
        }

        /**
         * Hanya TABLES
         */

        if (
            section !== "TABLES"
        ) {
            continue;
        }

        /**
         * ----------------------------------------------------
         * New LAYER record
         * ----------------------------------------------------
         */

        if (
            pair.code === 0 &&
            pair.value === "LAYER"
        ) {
            currentLayer = {
                name: null,
                aciColor: null,
                trueColor: null
            };

            continue;
        }

        if (!currentLayer) {
            continue;
        }

        /**
         * Layer name
         */

        if (
            pair.code === 2
        ) {
            currentLayer.name =
                pair.value;

            continue;
        }

        /**
         * ACI color
         */

        if (
            pair.code === 62
        ) {
            const color =
                Number(pair.value);

            if (
                Number.isFinite(color)
            ) {
                currentLayer.aciColor =
                    color;
            }

            continue;
        }

        /**
         * True color
         */

        if (
            pair.code === 420
        ) {
            const color =
                Number(pair.value);

            if (
                Number.isFinite(color)
            ) {
                currentLayer.trueColor =
                    color;
            }

            continue;
        }

        /**
         * Bila record selesai,
         * simpan saat menemukan record berikutnya.
         */

        if (
            pair.code === 0
        ) {
            saveLayer(
                layers,
                currentLayer
            );

            currentLayer = null;
        }
    }

    /**
     * Save last layer
     */

    saveLayer(
        layers,
        currentLayer
    );

    return layers;
}


/**
 * ============================================================
 * Save Layer
 * ============================================================
 */

function saveLayer(
    layers,
    layer
) {
    if (
        !layer ||
        !layer.name
    ) {
        return;
    }

    layers.set(
        layer.name,
        {
            name: layer.name,
            aciColor:
                layer.aciColor,
            trueColor:
                layer.trueColor
        }
    );
}


/**
 * ============================================================
 * Parse ENTITIES
 * ============================================================
 */

function parseEntities(
    pairs,
    layers
) {
    const entities = new Map();

    let section = null;
    let entity = null;

    for (let i = 0; i < pairs.length; i++) {
        const pair = pairs[i];

        /**
         * ----------------------------------------------------
         * SECTION
         * ----------------------------------------------------
         */

        if (
            pair.code === 0 &&
            pair.value === "SECTION"
        ) {
            const next =
                pairs[i + 1];

            if (
                next &&
                next.code === 2
            ) {
                section =
                    String(next.value)
                        .toUpperCase();
            }

            continue;
        }

        /**
         * ----------------------------------------------------
         * ENDSEC
         * ----------------------------------------------------
         */

        if (
            pair.code === 0 &&
            pair.value === "ENDSEC"
        ) {
            /**
             * Save entity terakhir.
             */

            saveEntityStyle(
                entities,
                entity,
                layers
            );

            entity = null;
            section = null;

            continue;
        }

        /**
         * Hanya ENTITIES
         */

        if (
            section !== "ENTITIES"
        ) {
            continue;
        }

        /**
         * ----------------------------------------------------
         * New entity
         * ----------------------------------------------------
         *
         * Group 0 = entity type
         */

        if (
            pair.code === 0
        ) {
            /**
             * Simpan entity sebelumnya.
             */

            saveEntityStyle(
                entities,
                entity,
                layers
            );

            entity = {
                type: pair.value,
                handle: null,
                layer: null,
                aciColor: null,
                trueColor: null
            };

            continue;
        }

        if (!entity) {
            continue;
        }

        /**
         * Entity handle
         */

        if (
            pair.code === 5
        ) {
            entity.handle =
                normalizeHandle(
                    pair.value
                );

            continue;
        }

        /**
         * Layer
         */

        if (
            pair.code === 8
        ) {
            entity.layer =
                pair.value;

            continue;
        }

        /**
         * ACI color
         */

        if (
            pair.code === 62
        ) {
            const color =
                Number(pair.value);

            if (
                Number.isFinite(color)
            ) {
                entity.aciColor =
                    color;
            }

            continue;
        }

        /**
         * True color
         */

        if (
            pair.code === 420
        ) {
            const color =
                Number(pair.value);

            if (
                Number.isFinite(color)
            ) {
                entity.trueColor =
                    color;
            }

            continue;
        }
    }

    /**
     * Save final entity
     */

    saveEntityStyle(
        entities,
        entity,
        layers
    );

    return entities;
}


/**
 * ============================================================
 * Save Entity Style
 * ============================================================
 */

function saveEntityStyle(
    entities,
    entity,
    layers
) {
    if (
        !entity ||
        !entity.handle
    ) {
        return;
    }

    const layer =
        entity.layer
            ? layers.get(
                entity.layer
            )
            : null;

    const resolved =
        resolveColor(
            entity,
            layer
        );

    entities.set(
        normalizeHandle(
            entity.handle
        ),
        {
            handle:
                normalizeHandle(
                    entity.handle
                ),

            type:
                entity.type,

            layer:
                entity.layer,

            aciColor:
                entity.aciColor,

            trueColor:
                entity.trueColor,

            layerAciColor:
                layer?.aciColor ??
                null,

            layerTrueColor:
                layer?.trueColor ??
                null,

            color:
                resolved.color,

            colorSource:
                resolved.source
        }
    );
}


/**
 * ============================================================
 * Resolve Color
 * ============================================================
 *
 * Prioritas:
 *
 * 420 entity
 * 62 entity
 * 420 layer
 * 62 layer
 * default
 */

function resolveColor(
    entity,
    layer
) {
    /**
     * True Color entity
     */

    if (
        isValidTrueColor(
            entity.trueColor
        )
    ) {
        return {
            color:
                trueColorToHex(
                    entity.trueColor
                ),

            source:
                "entity-true-color"
        };
    }

    /**
     * Entity ACI
     */

    if (
        entity.aciColor !== null &&
        entity.aciColor !== undefined &&
        entity.aciColor !== 0 &&
        entity.aciColor !== 256
    ) {
        return {
            color:
                aciToHex(
                    entity.aciColor
                ),

            source:
                "entity-aci"
        };
    }

    /**
     * BYLAYER
     *
     * 256 = BYLAYER
     */

    if (
        entity.aciColor === 256 ||
        entity.aciColor === null ||
        entity.aciColor === undefined
    ) {
        if (
            layer &&
            isValidTrueColor(
                layer.trueColor
            )
        ) {
            return {
                color:
                    trueColorToHex(
                        layer.trueColor
                    ),

                source:
                    "layer-true-color"
            };
        }

        if (
            layer &&
            layer.aciColor !== null &&
            layer.aciColor !== undefined
        ) {
            return {
                color:
                    aciToHex(
                        layer.aciColor
                    ),

                source:
                    "layer-aci"
            };
        }
    }

    /**
     * BYBLOCK
     *
     * Belum punya block context.
     * Gunakan default sementara.
     */

    if (
        entity.aciColor === 0
    ) {
        return {
            color:
                DEFAULT_COLOR,

            source:
                "by-block-default"
        };
    }

    /**
     * Default
     */

    return {
        color:
            DEFAULT_COLOR,

        source:
            "default"
    };
}


/**
 * ============================================================
 * Get style by EntityHandle
 * ============================================================
 */

export function getEntityStyle(
    styleInfo,
    entityHandle
) {
    if (
        !styleInfo ||
        !styleInfo.entities
    ) {
        return null;
    }

    const handle =
        normalizeHandle(
            entityHandle
        );

    return (
        styleInfo.entities.get(
            handle
        ) || null
    );
}


/**
 * ============================================================
 * Get layer style
 * ============================================================
 */

export function getLayerStyle(
    styleInfo,
    layerName
) {
    if (
        !styleInfo ||
        !styleInfo.layers
    ) {
        return null;
    }

    return (
        styleInfo.layers.get(
            layerName
        ) || null
    );
}


/**
 * ============================================================
 * Normalize Entity Handle
 * ============================================================
 */

function normalizeHandle(
    value
) {
    return String(value ?? "")
        .trim()
        .toUpperCase();
}


/**
 * ============================================================
 * True Color
 * ============================================================
 *
 * DXF 420:
 *
 * 0x00BBGGRR
 */

function trueColorToHex(
    value
) {
    const number =
        Number(value);

    if (
        !Number.isFinite(number)
    ) {
        return DEFAULT_COLOR;
    }

    const r =
        number & 0xFF;

    const g =
        (number >> 8) & 0xFF;

    const b =
        (number >> 16) & 0xFF;

    return rgbToHex(
        r,
        g,
        b
    );
}


/**
 * ============================================================
 * Validate True Color
 * ============================================================
 */

function isValidTrueColor(
    value
) {
    const number =
        Number(value);

    return (
        Number.isFinite(number) &&
        number >= 0 &&
        number <= 16777215
    );
}


/**
 * ============================================================
 * AutoCAD ACI → HEX
 * ============================================================
 *
 * Warna ACI utama AutoCAD.
 *
 * Untuk index yang umum:
 *
 * 1 = Red
 * 2 = Yellow
 * 3 = Green
 * 4 = Cyan
 * 5 = Blue
 * 6 = Magenta
 * 7 = White
 * 8 = Dark Gray
 * 9 = Light Gray
 */

function aciToHex(
    index
) {
    let aci =
        Number(index);

    if (
        !Number.isFinite(aci)
    ) {
        return DEFAULT_COLOR;
    }

    aci =
        Math.abs(
            Math.trunc(aci)
        );

    /**
     * Standard AutoCAD colors.
     */

    const standard = {
        1: "#FF0000",
        2: "#FFFF00",
        3: "#00FF00",
        4: "#00FFFF",
        5: "#0000FF",
        6: "#FF00FF",
        7: "#FFFFFF",
        8: "#414141",
        9: "#808080"
    };

    if (
        standard[aci]
    ) {
        return standard[aci];
    }

    /**
     * 0 / 256
     */

    if (
        aci === 0 ||
        aci === 256
    ) {
        return DEFAULT_COLOR;
    }

    /**
     * AutoCAD's indexed palette contains
     * 256 entries.
     *
     * Common palette values below.
     */

    const palette = {
        10: "#FF0000",
        11: "#FF7F7F",
        12: "#CC0000",
        13: "#CC6666",
        14: "#990000",
        15: "#995050",
        16: "#7F0000",
        17: "#7F3F3F",
        18: "#4C0000",
        19: "#4C2626",

        20: "#FF3F00",
        21: "#FF9F7F",
        22: "#CC3200",
        23: "#CC7F66",
        24: "#992500",
        25: "#995F4C",
        26: "#7F1F00",
        27: "#7F4F3F",
        28: "#4C1300",
        29: "#4C2F26",

        30: "#FF7F00",
        31: "#FFBF7F",
        32: "#CC6600",
        33: "#CC9966",
        34: "#994C00",
        35: "#99734C",
        36: "#7F3F00",
        37: "#7F5F3F",
        38: "#4C2600",
        39: "#4C3926",

        40: "#FFBF00",
        41: "#FFDF7F",
        42: "#CC9900",
        43: "#CCB266",
        44: "#997300",
        45: "#99864C",
        46: "#7F5F00",
        47: "#7F6F3F",
        48: "#4C3900",
        49: "#4C4326",

        50: "#FFFF00",
        51: "#FFFF7F",
        52: "#CCCC00",
        53: "#CCCC66",
        54: "#999900",
        55: "#99994C",
        56: "#7F7F00",
        57: "#7F7F3F",
        58: "#4C4C00",
        59: "#4C4C26",

        60: "#BFFF00",
        61: "#DFFF7F",
        62: "#99CC00",
        63: "#B2CC66",
        64: "#739900",
        65: "#86994C",
        66: "#5F7F00",
        67: "#6F7F3F",
        68: "#394C00",
        69: "#434C26",

        70: "#7FFF00",
        71: "#BFFF7F",
        72: "#66CC00",
        73: "#99CC66",
        74: "#4C9900",
        75: "#73994C",
        76: "#3F7F00",
        77: "#5F7F3F",
        78: "#264C00",
        79: "#394C26",

        80: "#3FFF00",
        81: "#9FFF7F",
        82: "#32CC00",
        83: "#7FCC66",
        84: "#269900",
        85: "#5F994C",
        86: "#1F7F00",
        87: "#4F7F3F",
        88: "#134C00",
        89: "#2F4C26",

        90: "#00FF00",
        91: "#7FFF7F",
        92: "#00CC00",
        93: "#66CC66",
        94: "#009900",
        95: "#4C994C",
        96: "#007F00",
        97: "#3F7F3F",
        98: "#004C00",
        99: "#264C26",

        100: "#00FF3F",
        101: "#7FFF9F",
        102: "#00CC32",
        103: "#66CC7F",
        104: "#009926",
        105: "#4C995F",
        106: "#007F1F",
        107: "#3F7F4F",
        108: "#004C13",
        109: "#264C2F",

        110: "#00FF7F",
        111: "#7FFFBF",
        112: "#00CC66",
        113: "#66CC99",
        114: "#00994C",
        115: "#4C9973",
        116: "#007F3F",
        117: "#3F7F5F",
        118: "#004C26",
        119: "#264C39",

        120: "#00FFBF",
        121: "#7FFFDF",
        122: "#00CC99",
        123: "#66CCB2",
        124: "#009973",
        125: "#4C9986",
        126: "#007F5F",
        127: "#3F7F6F",
        128: "#004C39",
        129: "#264C43",

        130: "#00FFFF",
        131: "#7FFFFF",
        132: "#00CCCC",
        133: "#66CCCC",
        134: "#009999",
        135: "#4C9999",
        136: "#007F7F",
        137: "#3F7F7F",
        138: "#004C4C",
        139: "#264C4C",

        140: "#00BFFF",
        141: "#7FDFFF",
        142: "#0099CC",
        143: "#66B2CC",
        144: "#007399",
        145: "#4C8699",
        146: "#005F7F",
        147: "#3F6F7F",
        148: "#00394C",
        149: "#26434C",

        150: "#007FFF",
        151: "#7FBFFF",
        152: "#0066CC",
        153: "#6699CC",
        154: "#004C99",
        155: "#4C7399",
        156: "#003F7F",
        157: "#3F5F7F",
        158: "#00264C",
        159: "#26394C",

        160: "#003FFF",
        161: "#7F9FFF",
        162: "#0032CC",
        163: "#667FCC",
        164: "#002699",
        165: "#4C5F99",
        166: "#001F7F",
        167: "#3F4F7F",
        168: "#00134C",
        169: "#262F4C",

        170: "#0000FF",
        171: "#7F7FFF",
        172: "#0000CC",
        173: "#6666CC",
        174: "#000099",
        175: "#4C4C99",
        176: "#00007F",
        177: "#3F3F7F",
        178: "#00004C",
        179: "#26264C",

        180: "#3F00FF",
        181: "#9F7FFF",
        182: "#3200CC",
        183: "#7F66CC",
        184: "#260099",
        185: "#5F4C99",
        186: "#1F007F",
        187: "#4F3F7F",
        188: "#13004C",
        189: "#2F264C",

        190: "#7F00FF",
        191: "#BF7FFF",
        192: "#6600CC",
        193: "#9966CC",
        194: "#4C0099",
        195: "#734C99",
        196: "#3F007F",
        197: "#5F3F7F",
        198: "#26004C",
        199: "#39264C",

        200: "#BF00FF",
        201: "#DF7FFF",
        202: "#9900CC",
        203: "#B266CC",
        204: "#730099",
        205: "#864C99",
        206: "#5F007F",
        207: "#6F3F7F",
        208: "#39004C",
        209: "#43264C",

        210: "#FF00FF",
        211: "#FF7FFF",
        212: "#CC00CC",
        213: "#CC66CC",
        214: "#990099",
        215: "#994C99",
        216: "#7F007F",
        217: "#7F3F7F",
        218: "#4C004C",
        219: "#4C264C",

        220: "#FF00BF",
        221: "#FF7FDF",
        222: "#CC0099",
        223: "#CC66B2",
        224: "#990073",
        225: "#994C86",
        226: "#7F005F",
        227: "#7F3F6F",
        228: "#4C0039",
        229: "#4C2643",

        230: "#FF007F",
        231: "#FF7FBF",
        232: "#CC0066",
        233: "#CC6699",
        234: "#99004C",
        235: "#994C73",
        236: "#7F003F",
        237: "#7F3F5F",
        238: "#4C0026",
        239: "#4C2639",

        240: "#FF003F",
        241: "#FF7F9F",
        242: "#CC0032",
        243: "#CC667F",
        244: "#990026",
        245: "#994C5F",
        246: "#7F001F",
        247: "#7F3F4F",
        248: "#4C0013",
        249: "#4C262F",

        250: "#333333",
        251: "#505050",
        252: "#696969",
        253: "#828282",
        254: "#BEBEBE",
        255: "#FFFFFF"
    };

    if (
        palette[aci]
    ) {
        return palette[aci];
    }

    /**
     * Fallback untuk index yang tidak dikenal.
     */

    return DEFAULT_COLOR;
}


/**
 * ============================================================
 * RGB → HEX
 * ============================================================
 */

function rgbToHex(
    r,
    g,
    b
) {
    return (
        "#" +
        toHex(r) +
        toHex(g) +
        toHex(b)
    );
}

function toHex(
    value
) {
    return Math.max(
        0,
        Math.min(
            255,
            Math.round(value)
        )
    )
        .toString(16)
        .padStart(2, "0")
        .toUpperCase();
}